import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { useTheme } from '../Theme/ThemeProvider';
import type { SettingsTab } from './UserProfilePopup';
import { PersonalizationTab } from './tabs/PersonalizationTab';
import { GeneralTab } from './tabs/GeneralTab';
import { AppsTab } from './tabs/AppsTab';
import { SecurityTab } from './tabs/SecurityTab';
import { AccountTab } from './tabs/AccountTab';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: SettingsTab;
  scrollToSection?: string;
}

const navItems: { id: SettingsTab; label: string; icon: string }[] = [
  { id: 'personalization', label: 'Personalization', icon: '/wrench.svg' },
];

export function SettingsModal({ open, onOpenChange, initialTab = 'personalization', scrollToSection }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const { isMobile } = useDeviceDetection();
  const { mode } = useTheme();
  const isLight = mode === 'light';
  const iconFilter = isLight ? 'brightness(0)' : 'none';

  // Reset to initialTab whenever modal opens
  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralTab />;
      case 'personalization':
        return <PersonalizationTab onClose={() => onOpenChange(false)} scrollToSection={scrollToSection} />;
      case 'apps':
        return <AppsTab />;
      case 'security':
        return <SecurityTab />;
      case 'account':
        return <AccountTab />;
      default:
        return null;
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[150] bg-black/40" />
        <Dialog.Content
          className={`fixed z-[151] flex flex-col shadow-xl overflow-hidden ${
            isMobile
              ? 'inset-0 rounded-none'
              : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl'
          }`}
          style={{
            background: 'var(--main-background)',
            border: isMobile ? 'none' : '1px solid var(--color-border)',
            width: isMobile ? '100%' : 680,
            height: isMobile ? '100%' : 400,
            maxHeight: isMobile ? '100%' : '85vh',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between shrink-0 px-6 py-4"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <Dialog.Title className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>
              Settings
            </Dialog.Title>
            <Dialog.Description className="sr-only">User settings and preferences.</Dialog.Description>
            <Dialog.Close asChild>
              <button
                className="rounded p-1 transition-colors"
                style={{ color: 'var(--color-text)' }}
                aria-label="Close"
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Cross2Icon width={18} height={18} />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-1 min-h-0">
            {/* Content */}
            <div className="flex-1 min-h-0 flex flex-col p-5">
              {renderTabContent()}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
